import { EntityId } from '@reduxjs/toolkit';
import { CouponOutDTO } from 'app/main/design-system/models/coupon.type';
import { RewardType } from 'app/main/design-system/models/reward.type';
import moment from 'moment';
import {
  DEFAULT_PARTICIPANT_IDENTIFICATION,
  DEFAULT_SHOW_REALTIME,
  DEFAULT_TIEBREAKER,
  WINNERS_COUNT,
} from './constants';
import type { PronoForm } from './ducks/prono.slice';

export enum SportType {
  FOOTBALL = 'football',
  BASKETBALL = 'basketball',
  RUGBY = 'rugby',
  TENNIS = 'tennis',
  LIBRE = 'libre',
}

export enum PronoStatus {
  DRAFT = 'draft',
  OPEN = 'open',
  CLOSED = 'closed',
  DRAWN = 'drawn',
}

export enum PronosticMode {
  BOTH = 'both',
  SCORE_ONLY = 'score_only',
  RESULT_ONLY = 'result_only',
}

export enum WinnerRule {
  EXACT = 'exact',
  BEST = 'best',
  ALL = 'all',
  MOST_VOTES = 'most_votes', // libre: the most-voted side wins
  MANUAL = 'manual', // libre: the merchant designates the winning side
}

export type WinningChoice = 'team1' | 'team2';

/** Manual override of registrations (takes precedence over the schedule). */
export enum ParticipationOverride {
  OPEN = 'open',
  CLOSED = 'closed',
}

/**
 * Displayed status, recomputed on the front end. The API only materializes the
 * closing on the next access/action on the prono: a prono still "open" whose
 * closing date has passed must appear "awaiting result", unless registrations
 * have been forced open manually.
 */
export const getDisplayPronoStatus = (
  status: PronoStatus,
  effectiveCloseAt?: string,
  participationOverride?: ParticipationOverride | null,
): PronoStatus => {
  if (
    status === PronoStatus.OPEN &&
    participationOverride !== ParticipationOverride.OPEN &&
    !!effectiveCloseAt &&
    new Date(effectiveCloseAt) < new Date()
  ) {
    return PronoStatus.CLOSED;
  }
  return status;
};

export type ParticipationMode = 'score' | 'result';
export type ResultChoice = 'team1' | 'draw' | 'team2';

export interface PronoRewardDTO {
  name: string;
  type?: RewardType; // optional during editing; guaranteed present after isPronoRewardValid
  points?: number; // if POINTS or COUPON_POINTS
  couponData?: CouponOutDTO; // if COUPON or COUPON_POINTS
}

export interface PronoStatsDTO {
  totalParticipants: number;
  exactScoreCount: number;
  resultOnlyCount: number;
  loyaltyUsersCount: number;
}

/** Creation payload (mirror of PronoGameCreate on the API side). */
export interface PronoGameOutDTO {
  storeIds: EntityId[];
  name: string;
  headerTitle: string;
  sport: SportType;
  team1: string;
  team2: string;
  matchDate: string;
  pronosticMode: PronosticMode;
  lot: PronoRewardDTO;
  winnerRule: WinnerRule;
  tiebreaker: string;
  winnersCount: number;
  participantIdentification: string;
  allowMultipleParticipations: boolean;
  openAt: string;
  closeAt: string;
  endMessage: string;
  showRealtime: string;
  smsWinnerEnabled: boolean;
}

/** Full response (mirror of PronoGameResponse). */
export interface PronoGameInDTO extends PronoGameOutDTO {
  id: EntityId;
  franchiseId: EntityId;
  slug: string;
  shareUrl: string;
  qrCodeUrl?: string;
  status: PronoStatus;
  participationOverride?: ParticipationOverride | null;
  stats: PronoStatsDTO;
  createdAt: string;
}

/** Partial edit payload (mirror of PronoGameUpdate on the API side). */
export type PronoUpdateDTO = Partial<PronoGameOutDTO>;

/** List view (mirror of PronoGameItemOutDAO). */
export interface PronoGameListItemDTO {
  id: EntityId;
  name: string;
  sport: SportType;
  team1: string;
  team2: string;
  matchDate: string;
  status: PronoStatus;
  dateCreated: string;
  stats: PronoStatsDTO;
  storeCount: number;
}

/** Paginated games page (mirror of the back end's paginated response). */
export interface PronoGamesPageDTO {
  games: PronoGameListItemDTO[];
  total: number;
  skip: number;
  limit: number;
}

/** Participation (mirror of ParticipationResponse). */
export interface ParticipationDTO {
  id: EntityId;
  pronoGameId: EntityId;
  firstName: string;
  phone: string;
  isLoyaltyUser: boolean;
  pronosticMode: ParticipationMode;
  scoreTeam1?: number;
  scoreTeam2?: number;
  resultChoice?: ResultChoice;
  isWinner: boolean;
  participatedAt: string;
}

export interface ParticipantsPageDTO {
  participants: ParticipationDTO[];
  total: number;
  skip: number;
  limit: number;
}

export interface ParticipantsQuery {
  skip?: number;
  limit?: number;
  mode?: ParticipationMode | 'all';
  search?: string;
}

/** Draw (mirror of PronoDrawResponse). */
export interface PronoDrawDTO {
  id: EntityId;
  pronoGameId: EntityId;
  realScoreTeam1?: number | null;
  realScoreTeam2?: number | null;
  winningChoice?: WinningChoice | null;
  winnerParticipationIds: EntityId[];
  eligibleCount: number;
  totalParticipants: number;
  drawnAt: string;
  smsNotificationSent: boolean;
  winners: ParticipationDTO[];
}

export interface PronoDrawRequestDTO {
  realScoreTeam1?: number;
  realScoreTeam2?: number;
  winningChoice?: WinningChoice;
}

/** Detailed statistics (mirror of PronoStatsResponse). */
export interface PronoStatsResponseDTO {
  totalParticipants: number;
  exactScoreCount: number;
  resultOnlyCount: number;
  loyaltyUsersCount: number;
  guestUsersCount: number;
  daysRemaining: number | null;
  status: PronoStatus;
}

/**
 * Checks that a reward is complete according to its type.
 * Type guard: narrows `lot` to PronoRewardDTO after validation.
 */
export const isPronoRewardValid = (reward?: PronoRewardDTO): reward is PronoRewardDTO => {
  if (!reward || !reward.name || !reward.type) {
    return false;
  }
  switch (reward.type) {
    case RewardType.POINTS:
      return !!reward.points && reward.points > 0;
    case RewardType.COUPON:
      return !!reward.couponData;
    case RewardType.POINTS_COUPON:
      return !!reward.points && reward.points > 0 && !!reward.couponData;
    default:
      return false;
  }
};

/** Format the date pickers read from, and write back to, the form fields (naive local time). */
export const LOCAL_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';

/**
 * Written to a date field when the picker receives keyboard input that cannot be parsed.
 * Discarding those keystrokes instead would leave the last valid date in the form, so a step
 * stayed valid while the field displayed something else — see `isLocalInput`, which rejects this.
 */
export const INVALID_LOCAL_INPUT = 'invalid';

/** Narrows to a genuine naive-local datetime, rejecting both '' and `INVALID_LOCAL_INPUT`. */
export const isLocalInput = (value?: string): value is string =>
  !!value && value !== INVALID_LOCAL_INPUT && moment(value, LOCAL_INPUT_FORMAT, true).isValid();

/** "Now" in the datetime-local field format (reliable lexicographic comparison). */
export const nowLocalInput = (): string => moment().format(LOCAL_INPUT_FORMAT);

/**
 * Converts a `datetime-local` field value (naive local time, e.g. "2030-05-29T09:00")
 * to ISO UTC for the API. Without this, Mongo stores the naive string as UTC, and reading it
 * back via `moment()` reconverts it to local -> timezone offset (e.g. +2h in summer in France).
 * Anything that is not a real local datetime yields '' rather than an "Invalid date" payload.
 */
export const localInputToISO = (value?: string): string =>
  isLocalInput(value) ? moment(value, LOCAL_INPUT_FORMAT).toISOString() : '';

/** Wizard "game" step: complete match info and match in the future. */
export const isPronoStepGameValid = (form: PronoForm): boolean =>
  !!(
    form.name?.trim() &&
    form.sport &&
    form.team1?.trim() &&
    form.team2?.trim() &&
    isLocalInput(form.matchDate) &&
    form.matchDate > nowLocalInput()
  );

/**
 * Wizard "duration" step: dates present and consistent
 * (openAt < closeAt <= matchDate — same rules as the API).
 * Lexicographic comparison: the `datetime-local` values share the YYYY-MM-DDTHH:mm format,
 * which `isLocalInput` guarantees — an unparsable field fails the step instead of comparing.
 */
export const isPronoStepDurationValid = (form: PronoForm): boolean => {
  if (!isLocalInput(form.openAt) || !isLocalInput(form.closeAt) || !isLocalInput(form.matchDate)) {
    return false;
  }
  return form.openAt < form.closeAt && form.closeAt <= form.matchDate;
};

/**
 * Projects a reward to the edit payload (PronoRewardConfig on the API side):
 * keeps only the editable fields, without the server fields (couponId).
 * Also serves as a comparison baseline for "has the reward changed?".
 */
export const pronoRewardToPayload = (lot: PronoRewardDTO): PronoRewardDTO => ({
  name: lot.name,
  type: lot.type,
  points: lot.points,
  couponData: lot.couponData,
});

/**
 * Builds the creation payload from the wizard form.
 * Throws an error if a required field is missing.
 */
export const convertPronoFormToDTO = (form: PronoForm): PronoGameOutDTO => {
  if (!form.name) {
    throw new Error('Prono name is required');
  }
  if (!form.team1 || !form.team2) {
    throw new Error('Both teams are required');
  }
  if (!form.matchDate) {
    throw new Error('Match date is required');
  }
  if (!isPronoRewardValid(form.lot)) {
    throw new Error('A valid reward is required');
  }
  if (!form.openAt || !form.closeAt) {
    throw new Error('Opening and closing dates are required');
  }
  return {
    storeIds: form.storeIds ?? [],
    name: form.name,
    // Product decision 2026-06-05: the title shown to the player = game name.
    headerTitle: form.name,
    sport: form.sport ?? SportType.FOOTBALL,
    team1: form.team1,
    team2: form.team2,
    matchDate: localInputToISO(form.matchDate),
    pronosticMode: form.pronosticMode ?? PronosticMode.SCORE_ONLY,
    lot: form.lot,
    winnerRule: form.winnerRule ?? WinnerRule.EXACT,
    tiebreaker: form.tiebreaker ?? DEFAULT_TIEBREAKER,
    winnersCount: form.winnersCount ?? WINNERS_COUNT.DEFAULT,
    participantIdentification: form.participantIdentification ?? DEFAULT_PARTICIPANT_IDENTIFICATION,
    allowMultipleParticipations: form.allowMultipleParticipations ?? false,
    openAt: localInputToISO(form.openAt),
    closeAt: localInputToISO(form.closeAt),
    endMessage: form.endMessage ?? '',
    showRealtime: form.showRealtime ?? DEFAULT_SHOW_REALTIME,
    // Winner SMS always on (product decision 2026-06-04): the field stays for the API.
    smsWinnerEnabled: true,
  };
};
